import { Reveal, Eyebrow, Check } from "@/components/Reveal";

const COHORT_IMG =
  "https://customer-assets-lqy194kg.emergentagent.net/job_brand-embodied/artifacts/ac435ecdb12ccd81_Screenshot%202026-10-07%20at%205.06.28%E2%80%AFPM.png";

const WEEKS = [
  {
    n: "01",
    theme: "Find your message",
    title: "Build a brand people remember.",
    intro:
      "Get clear on what you stand for, what you believe, and what you want to be known for.",
    leave: [
      "Your one-sentence brand message",
      "Your unique point of view",
      "Who you're here to help",
      "Your creator identity",
    ],
  },
  {
    n: "02",
    theme: "Claim your voice",
    title: "Stop hiding behind who you think you should be.",
    intro:
      "Identify the patterns that keep you second-guessing yourself and build the capacity to actually be seen.",
    leave: [
      "Your visibility triggers",
      "Your perfectionism + fear patterns",
      "A personal embodiment practice",
      "Tools for moving through resistance",
    ],
  },
  {
    n: "03",
    theme: "Own your position",
    title: "Make it obvious why people should follow you.",
    intro:
      "Turn your ideas and experience into a brand people can understand within seconds.",
    leave: [
      "Your positioning statement",
      "Your ideal audience",
      "3–5 content pillars",
      "Your Instagram bio + profile structure",
      "Your “what I talk about” framework",
    ],
  },
  {
    n: "04",
    theme: "Become visible",
    title: "Stop waiting until you're ready to be seen.",
    intro:
      "We stop talking about visibility and actually practice it — creating and publishing while working through discomfort.",
    leaveLabel: "We'll work through",
    leave: [
      "Comparison",
      "Judgment",
      "Perfectionism",
      "Low engagement",
      "Feeling cringe",
      "Fear of being misunderstood",
    ],
    note: "The goal isn't to eliminate fear. It's to stop letting fear make your decisions.",
  },
  {
    n: "05",
    theme: "Build your content engine",
    title: "Know what to say for the next 90 days.",
    intro:
      "Build a content system around your voice, ideas, and brand — so you're never wondering what to post.",
    leave: [
      "A 90-day content bank",
      "50+ personalized content ideas",
      "20+ hooks in your voice",
      "3–5 repeatable content formats",
      "A sustainable posting rhythm",
      "Natural CTA ideas",
    ],
  },
  {
    n: "06",
    theme: "Create your offer",
    title: "Turn what you know into something people can buy.",
    intro:
      "Turn your expertise into a real, testable offer without spending months overbuilding it.",
    leave: [
      "The problem you solve",
      "Who it's for + the transformation",
      "Your offer name + deliverables",
      "Your price",
      "Your simple pitch",
    ],
    note: "You'll test the idea with real people before overbuilding it.",
  },
  {
    n: "07",
    theme: "Learn to sell",
    title: "Sell your work without selling your soul.",
    intro:
      "Make sales feel less like a performance and more like a conversation.",
    leave: [
      "A simple DM framework",
      "Your sales conversation framework",
      "Objection + follow-up responses",
      "Your offer pitch",
      "Live practice asking for the sale",
    ],
    note: "Confidence isn't something you wait for. You build it through reps.",
  },
  {
    n: "08",
    theme: "Launch & keep going",
    title: "Turn what you've built into your next 90 days.",
    intro:
      "Leave with a clear plan for launching and continuing to build after the program ends.",
    leave: [
      "Your launch timeline",
      "Launch content + CTA",
      "Your sales + follow-up process",
      "Your next 90 days mapped out",
      "A repeatable system for creating, selling, and growing",
    ],
  },
];

const INCLUDED = [
  "Weekly live group calls",
  "Hot-seat coaching on your brand",
  "A private community of builders",
  "Feedback between calls",
];

export const Inside = () => (
  <section
    id="inside"
    className="bg-white py-24 lg:py-32"
    data-testid="inside-section"
  >
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal>
        <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <div>
            <Eyebrow light>What&rsquo;s inside</Eyebrow>
            <h2 className="mt-6 max-w-xl font-mont text-3xl font-bold leading-[1.15] tracking-tight text-ink sm:text-4xl lg:text-5xl">
              8 weeks. A brand, an offer, and 90 days of{" "}
              <em className="italic text-forest">momentum</em>.
            </h2>
          </div>
          <p className="max-w-sm font-mont text-sm italic leading-relaxed text-stone">
            Every week builds on the last. You leave with a brand, an offer,
            and a system you can keep building.
          </p>
        </div>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="mt-14 border-y border-ink/10">
          {WEEKS.map((w, i) => (
            <div
              key={w.n}
              className={`relative py-10 pl-14 pr-6 sm:pl-24 sm:pr-12 lg:py-12 ${
                i % 2 === 0 ? "bg-bone" : "bg-white"
              }`}
              data-testid={`pillar-week-${i + 1}`}
            >
              <span
                className="pointer-events-none absolute right-6 top-6 select-none font-serif text-6xl italic text-ink/[0.06] sm:text-8xl"
                aria-hidden="true"
              >
                {w.n}
              </span>
              {i < WEEKS.length - 1 && (
                <span
                  className="absolute bottom-0 left-[23px] top-12 w-px bg-forest/25 sm:left-[39px]"
                  aria-hidden="true"
                />
              )}
              <span
                className="absolute left-4 top-10 h-[15px] w-[15px] rounded-full border-2 border-forest bg-white sm:left-8"
                aria-hidden="true"
              />
              <div className="relative grid grid-cols-1 gap-4 lg:grid-cols-12 lg:gap-10">
                <div className="lg:col-span-4">
                  <p className="font-serif text-sm italic text-forest">
                    Week {w.n} — {w.theme}
                  </p>
                  <h3 className="mt-3 font-mont text-2xl font-bold text-ink">
                    {w.title}
                  </h3>
                </div>
                <div className="lg:col-span-8">
                  {w.intro && (
                    <p className="max-w-xl font-mont text-sm leading-relaxed text-[#5c5c5c]">
                      {w.intro}
                    </p>
                  )}
                  {w.leave.length > 0 && (
                    <>
                      <p
                        className={`text-[11px] uppercase tracking-[0.22em] text-stone ${
                          w.intro ? "mt-6" : ""
                        }`}
                      >
                        {w.leaveLabel || "You leave with"}
                      </p>
                      <ul className="mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                        {w.leave.map((item) => (
                          <li
                            key={item}
                            className="flex items-start gap-3 font-mont text-sm leading-relaxed text-[#5c5c5c]"
                          >
                            <Check className="mt-0.5" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
          <div className="bg-ink p-8 lg:p-10">
            <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-center">
              <div className="max-w-2xl">
                <h3 className="mt-3 font-mont text-2xl font-bold text-bone sm:text-3xl">
                  Becoming an Embodied Creator
                </h3>
                <p className="mt-4 font-mont text-sm leading-relaxed text-white/60">
                  Underneath it all: nervous-system tools and a humane pace.
                  Ambition without self-abandonment — because brands built on
                  burnout don&rsquo;t last.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:min-w-[380px]">
                {INCLUDED.map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <Check />
                    <span className="font-mont text-xs font-bold uppercase tracking-[0.16em] text-white/70">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-8 border-t border-white/10 pt-8">
              <img
                src={COHORT_IMG}
                alt="The cohort on a weekly live call"
                data-testid="cohort-call-photo"
                className="img-bw w-full border border-white/10 object-cover"
              />
              <p className="mt-4 font-serif text-sm italic text-white/50">
                Weekly live calls with your cohort.
              </p>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export default Inside;
